import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "@/locales/en.json";
import mr from "@/locales/mr.json";

/**
 * Localisation (spec §11.4).
 *
 * §11.4: "Default locale `en`. Second locale `mr` (Marathi). Build i18n from
 * the first screen — never hardcode a user-facing string in a component."
 * Initialised in `main.tsx` before the first render, so no component can mount
 * against an uninitialised i18n instance and fall back to raw keys.
 */

export const SUPPORTED_LOCALES = ["en", "mr"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** §11.4 — "Default locale `en`." */
export const DEFAULT_LOCALE: Locale = "en";

const STORAGE_KEY = "steelbill.locale";

/**
 * The two-option toggle's labels (§11.4).
 *
 * "Put the toggle somewhere reachable **without reading English** — use the
 * 'अ / A' glyph pair as the icon." The glyphs ARE the affordance: the owner
 * recognises the Devanagari अ without reading a word, which is the entire point
 * for someone who prefers Marathi labels. `label` is each language's name in
 * ITS OWN script — never "Marathi", which is only readable by someone who
 * already reads English.
 */
export const LOCALE_OPTIONS: readonly { value: Locale; glyph: string; label: string }[] = [
  { value: "en", glyph: "A", label: "English" },
  { value: "mr", glyph: "अ", label: "मराठी" },
];

function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * The stored preference, or the default.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `navigator.language` IS DELIBERATELY NOT CONSULTED — not as a fallback, not
 * as a hint, nowhere.
 *
 * §11.4: "Do NOT rely on `navigator.language`: his devices are likely set to
 * English even if he prefers Marathi labels." Auto-detection would therefore be
 * wrong for the one user it is meant to help, AND it would be unstable: the
 * office desktop and the workshop phone could disagree, so the app would speak
 * a different language depending on which one he picked up. An explicit choice
 * persisted to `localStorage` is stable and correctable in one tap.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function getStoredLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* private mode, locked-down WebView — fall through to the default */
  }
  return DEFAULT_LOCALE;
}

/**
 * Switch language, persist it, and tell the document.
 *
 * `<html lang>` matters: a screen reader announces Marathi with an English
 * voice otherwise, and the browser picks the wrong font-fallback and line
 * breaking rules for Devanagari.
 */
export async function setLocale(locale: Locale): Promise<void> {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* the switch must still work even if it cannot be remembered */
  }
  document.documentElement.lang = locale;
  await i18n.changeLanguage(locale);
}

/**
 * Initialise. Idempotent, so a test that imports this twice does not warn.
 */
export function initI18n(locale: Locale = getStoredLocale()): typeof i18n {
  if (!i18n.isInitialized) {
    void i18n.use(initReactI18next).init({
      resources: {
        en: { translation: en },
        mr: { translation: mr },
      },
      lng: locale,
      /**
       * §11.4 makes `en` the default and requires a CI check that `mr.json` has
       * every key `en.json` has (there is one in `src/__tests__/locales.test.ts`).
       * `fallbackLng: "en"` is the runtime half of the same promise: a key that
       * slips through shows ENGLISH, which the owner can act on, rather than a
       * raw `btn.newInvoice`, which he cannot.
       */
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      /**
       * OFF. i18next's default escaping is for frameworks that inject strings
       * as HTML; React already escapes everything it renders, and leaving this
       * on double-escapes an apostrophe in "Shubham Engineering Work's" into
       * `&#39;`.
       */
      interpolation: { escapeValue: false },
      /**
       * A missing key returns the KEY itself (i18next's default) — visible in
       * review, harmless in production, and never `undefined` mid-render.
       */
      /** Keys are `btn.newInvoice` / `lbl.customer` / `err.noInternet` (§11.4). */
      keySeparator: ".",
      nsSeparator: false,
      debug: false,
    });
  }
  document.documentElement.lang = i18n.language;
  return i18n;
}

export default i18n;
